import { CreateAccountResponse } from "@/features/auth/types";
import { getJwt } from "@/features/auth/utils";
import type { ConfirmationResponse } from "@/features/register/types";
import { AuthApiClient } from "@/features/user/api-client/api-client";
import type {
  legal_form_type,
  NaturalPersonMissingFields,
  StaticActivity,
  StaticCSP,
  UserType
} from "@/features/user/types";
import { DEFAULT_STALE_TIME } from "@/features/utils/fetch";
import type { AccountCreate, NaturalEntityModify } from "@/features/utils/types";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import _ from "lodash";
import { UserCguApiClient } from "../cgu/api-client/user-cgu-api-client";
import { pushOtherToEnd } from "@/utils/array.utils";

export const useUser = () => {
  const queryClient = useQueryClient();
  const useUserProfile = (props?: UseQueryOptions<UserType>) => {
    return useQuery<UserType>({
      queryKey: props?.queryKey ?? ["user", "profile"],
      queryFn: () => AuthApiClient.profile(),
      staleTime: 0,
      refetchOnMount: false,
      gcTime: DEFAULT_STALE_TIME,
      enabled: props?.enabled ?? !!getJwt()?.token,
      ..._.omit(props, "queryKey", "enabled")
    });
  };

  const useStaticData = (locale: string) =>
    useQuery({
      queryKey: ["static-data", locale],
      queryFn: () => AuthApiClient.getStaticData(locale),
      staleTime: Infinity
    });

  const useCountries = (locale: string) =>
    useQuery({
      queryKey: ["static-data", "countries", locale],
      queryFn: () => AuthApiClient.getStaticCountries(locale),
      gcTime: Infinity,
      staleTime: Infinity
    });

  const useCguCheckSave = (
    options?: UseMutationOptions<ConfirmationResponse, Error, { TermUuid: string }>
  ) =>
    useMutation<ConfirmationResponse, Error, { TermUuid: string }>({
      mutationFn: async ({ TermUuid }) => await UserCguApiClient.saveRequestCGU(TermUuid),
      ...options
    });

  const useCgusavedQuery = () =>
    useQuery({
      queryKey: ["user-cgu"],
      queryFn: async () => await UserCguApiClient.getUserRequestCGU(),
      enabled: !!getJwt()?.token,
      refetchOnWindowFocus: false
    });

  const useCSP = (
    locale: string,
    configOptions?: UseQueryOptions<
      {
        cspCodes: StaticCSP[];
      },
      null
    >
  ) =>
    useQuery({
      queryKey: ["static-data", "CSP", locale],
      queryFn: () => AuthApiClient.getStaticCSP(locale),
      staleTime: Infinity,
      gcTime: Infinity,
      ...configOptions
    });

  const useNatureMandate = (locale: string, enable?: boolean) =>
    useQuery({
      queryKey: ["mandate", "nature", locale],
      queryFn: async () => await AuthApiClient.getStaticNatureMandate(),
      enabled: enable ?? true,
      staleTime: Infinity,
      gcTime: Infinity
    });

  const useMandateService = (locale: string, enable?: boolean) =>
    useQuery({
      queryKey: ["mandate", "service", locale],
      queryFn: async () => await AuthApiClient.getStaticMandateService(),
      enabled: enable ?? true,
      staleTime: Infinity,
      gcTime: Infinity
    });

  const useActivities = (
    locale: string,
    configOptions?: UseQueryOptions<
      {
        activities: StaticActivity[];
      },
      null
    >
  ) =>
    useQuery({
      queryKey: ["static-data", "activities", locale],
      queryFn: () => AuthApiClient.getStaticActivities(locale),
      staleTime: Infinity,
      gcTime: Infinity,

      ...configOptions
    });

  const useActivitiesNace = (
    locale: string,
    configOptions?: UseQueryOptions<
      {
        activities_nace: StaticActivity[];
      },
      null
    >
  ) =>
    useQuery<
      {
        activities_nace: StaticActivity[];
      },
      null
    >({
      queryKey: ["static-data", "activities_nace", locale],
      queryFn: async () => await AuthApiClient.getActivitiesNace(locale),
      select: (data: { activities_nace: StaticActivity[] }) => ({
        activities_nace: pushOtherToEnd(data.activities_nace, "NomEn")
      }),
      staleTime: Infinity,
      gcTime: Infinity,

      ...configOptions
    });

  const useLegalStatus = (locale: string) =>
    useQuery({
      queryKey: ["static-data", "legalStatus", locale],
      queryFn: () => AuthApiClient.getStaticLegalStatus(locale),
      staleTime: Infinity
    });

  const useLegalNace = (locale: string) =>
    useQuery({
      queryKey: ["static-data", "nace", locale],
      queryFn: () => AuthApiClient.getStaticNace(locale),
      staleTime: Infinity,
      gcTime: Infinity
    });

  const useLegalForm = () =>
    useQuery({
      queryKey: ["static-data", "legal_form"],
      queryFn: () => AuthApiClient.getLegalForms(),
      select: (data: legal_form_type[]) => pushOtherToEnd(data, "name"),
      staleTime: Infinity,
      gcTime: Infinity
    });

  const useEditProfileMissingFields = (
    options?: UseMutationOptions<ConfirmationResponse, Error, NaturalPersonMissingFields>
  ) =>
    useMutation<ConfirmationResponse, Error, NaturalPersonMissingFields>({
      mutationFn: async (naturalEntity) => AuthApiClient.editProfileMissingFields(naturalEntity),
      onSettled: () => queryClient.invalidateQueries({ queryKey: ["user", "profile"] }),
      ...options
    });

  const useEditProfile = (
    options?: UseMutationOptions<ConfirmationResponse, Error, NaturalEntityModify>
  ) =>
    useMutation<ConfirmationResponse, Error, NaturalEntityModify>({
      mutationFn: async (naturalEntity) => AuthApiClient.editProfile(naturalEntity),
      ...options
    });

  const useCreateProfile = (
    options?: UseMutationOptions<CreateAccountResponse, Error, AccountCreate>
  ) =>
    useMutation<CreateAccountResponse, Error, AccountCreate>({
      mutationFn: async (naturalEntity) => AuthApiClient.createAccount(naturalEntity),
      onSettled: () => queryClient.invalidateQueries({ queryKey: ["user", "profile"] }),
      ...options
    });

  return {
    useUserProfile,
    useStaticData,
    useCSP,
    useActivities,
    useLegalStatus,
    useLegalNace,
    useEditProfile,
    useCreateProfile,
    useCountries,
    useCguCheckSave,
    useCgusavedQuery,
    useNatureMandate,
    useLegalForm,
    useMandateService,
    useEditProfileMissingFields,
    useActivitiesNace
  };
};
